import React, { useEffect, useId, useMemo, useRef } from 'react';
import { getCalendarImage, getCurrentTrainer } from '../services/TrainerAssets';
import { t } from '../i18n';
import beepImage from '../assets/players/Beep.webp';
import './TrainingHero.css';

const workoutIcon = `${process.env.PUBLIC_URL || ''}/icon-192.png`;

function TrainingHero({
  streak = 0,
  hasPracticedToday = false,
  isVacationToday = false,
  todayPoints = null,
  isLoading = false,
  isStarting = false,
  onStart,
  unavailable = false,
}) {
  const headingId = useId();
  const currentTrainer = getCurrentTrainer();
  const numericStreak = Number(streak);
  const safeStreak = Number.isFinite(numericStreak) ? Math.max(0, Math.floor(numericStreak)) : 0;
  const pointsLabel = todayPoints != null && Number.isFinite(Number(todayPoints))
    ? `${Number(todayPoints).toLocaleString()}pt` : '—';
  const hasStreakData = !isLoading && !unavailable;
  const lastKnownPresentation = useRef(null);
  const vacationVisible = hasStreakData ? isVacationToday && !hasPracticedToday : lastKnownPresentation.current?.isVacationToday || false;
  const trainerStatus = hasStreakData
    ? (hasPracticedToday ? 'cheerful' : safeStreak > 0 ? 'depressed' : 'normal')
    : lastKnownPresentation.current?.trainerStatus || 'normal';
  const promptCategory = hasStreakData
    ? (hasPracticedToday ? 'completed' : safeStreak === 0 ? 'fresh' : 'active')
    : lastKnownPresentation.current?.promptCategory || 'pending';
  useEffect(() => {
    if (hasStreakData) lastKnownPresentation.current = { trainerStatus, promptCategory, isVacationToday: vacationVisible };
  }, [hasStreakData, trainerStatus, promptCategory, vacationVisible]);
  // Keep encouragement stable while requests or unrelated dashboard data update.
  const promptKey = useMemo(() => (
    promptCategory === 'completed'
      ? 'dashboard.trainingHero.completedPrompt'
      : promptCategory === 'pending' ? 'dashboard.streak.active0'
        : `dashboard.streak.${promptCategory}${Math.floor(Math.random() * 4)}`
  ), [promptCategory]);

  return (
    <section className="training-hero" aria-labelledby={headingId}>
      <h2 id={headingId} className="training-hero-title">{t('dashboard.trainingHero.title')}</h2>

      <div className="training-hero-portrait">
        <img
          className="training-hero-trainer"
          src={vacationVisible ? beepImage : getCalendarImage(trainerStatus)}
          alt={vacationVisible ? t('streakProtection.beep') : t('dashboard.streak.trainer', undefined, {
            name: currentTrainer.name,
            status: t(`dashboard.streak.${trainerStatus}`),
          })}
        />
      </div>

      {/* The home trainer's dialogue is learning content; surrounding UI stays localized. */}
      <blockquote className="training-hero-quote" lang="en">
        <span className="training-hero-quote-text" lang="en">{vacationVisible ? "You're on vacation today." : t(promptKey, 'en')}</span>
      </blockquote>

      <div className="training-hero-streak" role="status" aria-live="polite" aria-atomic="true">
        <span className="training-hero-streak-label">{t('dashboard.trainingHero.streakLabel')}</span>
        {isLoading ? (
          <span className="training-hero-status-message">{t('dashboard.streak.loading')}</span>
        ) : unavailable ? (
          <span className="training-hero-status-message">{t('dashboard.trainingHero.unavailable')}</span>
        ) : (
          <>
            <strong className="training-hero-streak-count">
              {t(safeStreak === 1 ? 'dashboard.streak.day' : 'dashboard.streak.days', undefined, { count: safeStreak })}
            </strong>
            <span className={`training-hero-today${hasPracticedToday ? ' is-complete' : ''}`}>
              <span className="training-hero-today-label">{t('dashboard.trainingHero.todayPoints')}</span>
              <strong className="training-hero-today-points">{pointsLabel}</strong>
            </span>
          </>
        )}
      </div>

      <button
        type="button"
        className="training-hero-start"
        onClick={onStart}
        disabled={isStarting}
        aria-busy={isStarting}
      >
        <img src={workoutIcon} alt="" aria-hidden="true" />
        <span>{t(isStarting ? 'dashboard.trainingHero.starting' : 'dashboard.startWorkout')}</span>
      </button>
    </section>
  );
}

export default TrainingHero;
